// --- React and Redux
import React from 'react';
import { useSelector } from 'react-redux';
import Button from '@dtci/spellbook/Button';
import { EditSectionForm } from './edit-form';
import { DetailsModal } from '../../../DetailsModal';
// --- Dialogs
import { useDeleteDialog} from "../../../dialogs/dialogs/delete";
import { useSaveDialog } from '../../../dialogs/dialogs/save';
// --- Utilities and APIs
import { useClearanceMemoTitlesActions } from '../../../../store/clearanceMemoTitles/useClearanceMemoTitles';
import { datastageApi } from '../../../../api';
import { useLoading } from '../../../../utils/hooks/useLoading';


/**
 * EditSectionModal Component
 *
 * This component renders a modal for editing a section of a clearance memo.
 * It includes a form for editing the section details,
 * buttons for saving or deleting the section,
 * and confirmation dialogs for these actions.
 *
 * @returns {Element}
 * @constructor
 */
export function EditSectionModal() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { isSectionModalOpen, section, title } = useSelector((s) => s.titles);
    const { openDeleteDialog } = useDeleteDialog();
    const { openSaveDialog } = useSaveDialog();
    const { updateClearanceMemoTitles } = useClearanceMemoTitlesActions();

    const closeModal = () => updateClearanceMemoTitles({ isSectionModalOpen: false });

    const saveSection = async () => {
        setLoadingTrue();

        try {
            await datastageApi.updateClearanceMemoSection(section);
            const toc = await datastageApi.clearanceMemoTOC(title?.id);
            updateClearanceMemoTitles({ clearanceMemoTOC: toc, isSectionModalOpen: false });
        } catch (error) {
            console.error('Error updating section:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleSave = async () => {
        if (section.isShared) {
            openSaveDialog(
                'Yes, Save Shared Section',
                [
                    'Are you sure you want to modify this shared section?',
                    'This will update all products with this shared section.',
                ],
                saveSection,
            );
        } else {
            await saveSection();
        }
    };

    const handleDelete = () => {
        openDeleteDialog(
            'Yes, Delete',
            ['Are you sure you want to delete this section?', 'This action cannot be reversed.'],
            async () => {
                setLoadingTrue();
                try {
                    await datastageApi.deleteSection(section?.id);
                    const toc = await datastageApi.clearanceMemoTOC(title?.id);
                    updateClearanceMemoTitles({ clearanceMemoTOC: toc, isSectionModalOpen: false });
                } catch (error) {
                    console.error('Error deleting section:', error);
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    return (
      <DetailsModal
            isOpen={isSectionModalOpen}
            onClose={closeModal}
            modalHeader="Edit Section"
            footer={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <div>
                        <Button
                            className="delete-button"
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-trash"
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </div>
                    <div>
                        <Button
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-ban"
                            onClick={closeModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-floppy-disk"
                            onClick={handleSave}
                        >
                            {section?.isShared ? 'Save Shared Section' : 'Save Section'}
                        </Button>
                    </div>
                </div>
            }
        >
            <EditSectionForm />
        </DetailsModal>
    );
}
